import axios, { AxiosResponse } from 'axios'

// Log the environment variables being used (without sensitive data)
console.log('API Client Configuration:', {
  baseURL: import.meta.env.VITE_API_URL,
  mode: import.meta.env.MODE
})

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,  // Use environment variable which includes /api
  timeout: 180000, // 3 minutes timeout for long-running requests like email generation
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Add request interceptor for auth token and logging
apiClient.interceptors.request.use(
  (config) => {
    // Handle API prefix based on environment
    console.log('Processing request URL:', config.url, 'Mode:', import.meta.env.MODE)
    if (config.url && config.url.startsWith('/api/')) {
      // In development with baseURL ending in /api, remove duplicate
      if (config.baseURL?.endsWith('/api')) {
        config.url = config.url.substring(4)
        console.log('🔧 DEV: Removed duplicate /api prefix, new URL:', config.url)
      }
      // In production, keep /api prefix for proper routing
      else {
        console.log('🔧 PRODUCTION: Keeping /api prefix for proper routing:', config.url)
      }
    }
    
    // Log the full URL being requested
    const fullUrl = new URL(config.url || '', config.baseURL).toString()
    console.log('Making API request to:', fullUrl)
    console.log('Request config:', {
      method: config.method,
      headers: config.headers,
      params: config.params,
      data: config.data
    })

    // Add any auth headers if needed
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('API Response success:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    })
    return response
  },
  (error) => {
    // Enhanced error logging
    console.error('=== API ERROR ===')
    console.error('API Error:', {
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      fullURL: error.config?.url ? new URL(error.config?.url || '', error.config?.baseURL || window.location.origin).toString() : 'unknown',
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      code: error.code,
      stack: error.stack
    })

    // Create user-friendly error message
    let userMessage = 'An error occurred. Please try again.'

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      userMessage = 'Request timed out. The operation is taking longer than expected. Please wait and try again.'
      console.error('Request timeout detected')
    } else if (error.response?.status === 401) {
      userMessage = 'Your session has expired. Please log in again.'
      // Clear any stored auth tokens
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      // Redirect to login
      setTimeout(() => {
        window.location.href = '/admin/login'
      }, 100)
    } else if (error.response?.status === 403) {
      userMessage = 'You do not have permission to access this resource.'
    } else if (error.response?.status === 404) {
      userMessage = 'The requested resource was not found.'
    } else if (error.response?.status === 500) {
      userMessage = 'Server error. Please try again later.'
    } else if (error.response?.status >= 400 && error.response?.status < 500) {
      userMessage = error.response?.data?.message || error.response?.data?.detail || 'Invalid request.'
    } else if (error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK' || !error.response) {
      userMessage = 'Network error. Please check your connection.'
    }

    console.error('User-friendly message:', userMessage)

    // Show user-friendly error message
    showErrorNotification(userMessage)

    // Modify error object to include user-friendly message
    error.userMessage = userMessage

    return Promise.reject(error)
  }
)

// Notification system
function showNotification(message: string, type: 'success' | 'error' = 'error') {
  // Create notification element
  const notification = document.createElement('div')
  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500'
  const icon = type === 'success'
    ? `<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>`
    : `<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>`

  notification.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-4 rounded-lg shadow-lg z-50 max-w-sm transition-all duration-300 transform translate-x-0`
  notification.innerHTML = `
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          ${icon}
        </svg>
        <span class="text-sm font-medium">${message}</span>
      </div>
      <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-white hover:text-gray-200">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
        </svg>
      </button>
    </div>
  `

  document.body.appendChild(notification)

  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (notification.parentNode) {
      notification.remove()
    }
  }, 5000)
}

function showErrorNotification(message: string) {
  showNotification(message, 'error')
}

function showSuccessNotification(message: string) {
  showNotification(message, 'success')
}

export type { AxiosResponse }
export { showSuccessNotification, showErrorNotification }
export default apiClient 